import { useState, useCallback, useRef, useEffect } from 'react';

interface UseSpeechRecognitionOptions {
    onTranscript: (text: string) => void;
}

interface SpeechRecognitionResult {
    isListening: boolean;
    isSupported: boolean;
    interimTranscript: string;
    error: string | null;
    toggle: () => void;
    stop: () => void;
}

export function useSpeechRecognition({ onTranscript }: UseSpeechRecognitionOptions): SpeechRecognitionResult {
    const [isListening, setIsListening] = useState(false);
    const [interimTranscript, setInterimTranscript] = useState('');
    const [error, setError] = useState<string | null>(null);
    const recognitionRef = useRef<any>(null);
    const onTranscriptRef = useRef(onTranscript);
    onTranscriptRef.current = onTranscript;

    const isSupported = typeof window !== 'undefined' &&
        ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

    const stop = useCallback(() => {
        recognitionRef.current?.stop();
        setIsListening(false);
        setInterimTranscript('');
    }, []);

    const toggle = useCallback(() => {
        if (!isSupported) {
            setError('Speech recognition is not supported in this browser');
            return;
        }

        if (isListening) {
            stop();
            return;
        }

        setError(null);
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-AU';
        recognition.continuous = true;
        recognition.interimResults = true;

        recognition.onresult = (event: any) => {
            let interim = '';
            let final = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    final += transcript;
                } else {
                    interim += transcript;
                }
            }
            setInterimTranscript(interim);
            if (final) {
                onTranscriptRef.current(final);
                setInterimTranscript('');
            }
        };

        recognition.onerror = (event: any) => {
            setError(`Speech error: ${event.error}`);
            setIsListening(false);
        };

        recognition.onend = () => {
            setIsListening(false);
            setInterimTranscript('');
        };

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
    }, [isListening, isSupported, stop]);

    useEffect(() => {
        return () => {
            recognitionRef.current?.stop();
        };
    }, []);

    return { isListening, isSupported, interimTranscript, error, toggle, stop };
}
